import React, { useEffect } from "react"
import { Navigate, useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom"
import { createPortal } from "react-dom"
import ProjectSessionDetail from "../../../components/DashboardComponents/ProjectSessionDetail"
import { ExitSessionModal, SessionCompletedModal } from "../../../components/DashboardComponents/ProjectSessions"
import { useSessionDetailPlayer } from "../../../components/FloatingPlayer"
import useSessionDetail from "../../../hooks/useSessionDetail"
import { projectTabReturnTo } from "../../../helpers/sessionReturnTo"

/**
 * Session detail as a project tab.
 *
 * If the floating widget is already showing a different in-progress
 * session, render this session inline so the widget stays put.
 * Otherwise open the global player and return to the sessions list.
 */
const EmbeddedSessionDetail = ({ sessionId, projectId, project, returnTo }) => {
  const navigate = useNavigate()
  const detail = useSessionDetail(sessionId, { project })

  const sessionsPath =
    returnTo || projectTabReturnTo("", "sessions", projectId) || `/projects/${projectId}/sessions`

  const goBack = () => navigate(sessionsPath)

  const handleCompletedClose = () => {
    detail.setIsCompletedOpen(false)
    navigate(sessionsPath, { replace: true })
  }

  const handleLeaveSession = async leavePayload => {
    await detail.onLeaveSession(leavePayload)
  }

  const sessionModals =
    typeof document === "undefined"
      ? null
      : createPortal(
          <>
            <ExitSessionModal
              isOpen={detail.isExitOpen}
              onClose={() => {
                if (!detail.isLeaving) detail.setIsExitOpen(false)
              }}
              project={{
                id: projectId || project?.id || null,
                title: project?.title || project?.name || "—",
                name: project?.name || project?.title || "—",
              }}
              onFinish={handleLeaveSession}
              isSubmitting={detail.isLeaving}
            />
            <SessionCompletedModal
              isOpen={detail.isCompletedOpen}
              onClose={handleCompletedClose}
              time={detail.completedStats.time}
              xp={detail.completedStats.xp}
              redirectHint="Redirecting to sessions..."
            />
          </>,
          document.body,
        )

  return (
    <>
      <ProjectSessionDetail
        session={detail.session}
        timer={detail.displayTimer}
        isLoading={detail.isLoading}
        error={detail.error}
        members={detail.members}
        gallery={detail.gallery}
        membersTotal={detail.membersTotal}
        membersPage={detail.membersPage}
        membersPageSize={detail.membersPageSize}
        isMembersLoading={detail.isMembersLoading}
        membersError={detail.membersError}
        onMembersPageChange={detail.onMembersPageChange}
        searchQuery={detail.searchQuery}
        onSearchChange={detail.onSearchChange}
        statusFilters={detail.statusFilters}
        onStatusFiltersChange={detail.onStatusFiltersChange}
        sortBy={detail.sortBy}
        onSortByChange={detail.onSortByChange}
        sortDirection={detail.sortDirection}
        onSortDirectionChange={detail.onSortDirectionChange}
        onExit={detail.onExit}
        onTimerToggle={detail.onTimerToggle}
        isTimerUpdating={detail.isTimerUpdating}
        onClose={goBack}
        alwaysShowClose
        projectId={projectId}
        project={project}
        currentUserId={detail.currentUserId}
        existingCollaboratorUserIds={detail.existingCollaboratorUserIds}
        onAddCollaborators={detail.onAddCollaborators}
        onRemoveCollaborator={detail.onRemoveCollaborator}
        isMutatingCollaborators={detail.isMutatingCollaborators}
        impressions={detail.impressions}
        reactingMemberId={detail.reactingMemberId}
        onToggleImpression={detail.onToggleImpression}
      />
      {sessionModals}
    </>
  )
}

const ProjectSessionDetailTab = () => {
  const { projectId, sessionId } = useParams()
  const location = useLocation()
  const { project } = useOutletContext()
  const { openSessionDetail, isOpen, sessionId: floatingSessionId } =
    useSessionDetailPlayer()

  const sessionsReturnTo = projectTabReturnTo(
    location.pathname,
    "sessions",
    projectId,
  )

  const isOtherSession = Boolean(
    isOpen &&
      floatingSessionId &&
      sessionId &&
      String(sessionId) !== String(floatingSessionId),
  )

  useEffect(() => {
    if (!sessionId || isOtherSession) return undefined
    openSessionDetail({
      sessionId,
      projectId,
      project,
      returnTo: sessionsReturnTo,
      returnLabel: "Redirecting to sessions...",
    })
    return undefined
  }, [
    sessionId,
    projectId,
    project,
    openSessionDetail,
    isOtherSession,
    sessionsReturnTo,
  ])

  if (isOtherSession) {
    return (
      <EmbeddedSessionDetail
        sessionId={sessionId}
        projectId={projectId}
        project={project}
        returnTo={sessionsReturnTo}
      />
    )
  }

  return (
    <Navigate
      to={sessionsReturnTo || `/projects/${projectId}/sessions`}
      replace
    />
  )
}

export default ProjectSessionDetailTab
